import * as R from 'ramda';
import { isDigit } from './Other';

export const replaceMultipleWords = (
  s: string,
  findWords: string[],
  replaceWords: string[],
  from: number,
  end: number = Number.MAX_SAFE_INTEGER
): string => {
  if (findWords.length !== replaceWords.length) {
    throw new Error(
      'replaceMultipleWords: findWords and replaceWords need to have same length'
    );
  }
  // hitta alla findWords
  let result = s;
  findWords.forEach((word, i) => {
    const regExp = new RegExp(`(^|[^a-ÿA-Ÿ0-9])(${word})(?=$|[^a-ÿA-Ÿ0-9])`);
    result = result.slice(from).replace(regExp, x => {
      if (x.length === word.length + 1) {
        // We have a leading whitespace
        return ' ' + replaceWords[i];
      } else {
        return replaceWords[i];
      }
    });
  });

  return result;
};

export interface IStringSplitResult {
  prefix: string;
  result: string;
  postfix: string;
}

export const splitStringAt = (
  s: string,
  from: number,
  len: number
): IStringSplitResult => {
  const prefix = s.slice(0, from);
  const postfix = s.slice(from + len);
  const result = s.slice(from, from + len);

  return {
    prefix,
    postfix,
    result,
  };
};

export const nextNumber = (
  s: string,
  index: number,
  validDecSeps: string[] = ['.', ','],
  allowExp: boolean = false,
  allowSign: boolean = false,
  decSep: string = ','
): { num: string; newIndex: number } => {
  let localIndex = index;
  if (allowSign && ['+', '-'].includes(s[localIndex])) {
    localIndex++;
  }

  while (
    localIndex < s.length &&
    R.union(
      ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'],
      validDecSeps
    ).includes(s[localIndex])
  ) {
    localIndex++;
  }
  if (allowExp && ['e', 'E'].includes(s[localIndex])) {
    localIndex++;
    if (['+', '-'].includes(s[localIndex])) {
      localIndex++;
    }
    while (isDigit(s[localIndex])) {
      localIndex++;
    }
  }

  let num = s.slice(index, localIndex);
  if (decSep && !R.isEmpty(R.difference(validDecSeps, [decSep]))) {
    num = replaceAllOccurancesChar(num, validDecSeps, decSep);
  }

  return {
    num,
    newIndex: localIndex,
  };
};

/**
 * Replaces all occurances of the characters in 'replace' with 'replaceWith'
 */
export const replaceAllOccurancesChar = (
  s: string,
  replace: string[],
  replaceWith: string
): string => {
  if (replace.some(c => c.length > 1)) {
    throw new Error(
      'replaceAllOccurancesChar trying to replace strings of longer length than 1'
    );
  }

  if (!replace) {
    return s;
  }

  let result = '';

  for (let i = 0; i < s.length; i++) {
    if (replace.includes(s[i])) {
      result += replaceWith;
    } else {
      result += s[i];
    }
  }

  return result;
};

/**
 * returns the index in s where the first none-whitespace character is.
 * Starts looking at 'index'
 *
 * Returns -1 if there are only whitespace in s
 */
export const skipWhiteSpace = (s: string, index: number = 0) => {
  while (index < s.length) {
    if (isWhiteSpace(s[index])) {
      index++;
    } else {
      return index;
    }
  }
  return index;
};

export const isUppercaseLetter = (char: string): boolean => {
  return isLetter(char) && char.toLocaleUpperCase() === char;
};

export const isLowercaseLetter = (char: string): boolean => {
  return isLetter(char) && char.toLocaleLowerCase() === char;
};

export const isLetter = (char: string): boolean => {
  return /^[a-ÿA-Ÿ]$/.test(char);
};

export const isAlphaNumerical = (char: string): boolean => {
  return /^[a-ÿA-Ÿ0-9]$/.test(char);
};

export const isWhiteSpace = (char: string): boolean => {
  return /^\s$/.test(char);
};

/**
 * Change part of a string to Locale UpperCase, beginning at index 'from' and 'len'
 * nr of characters
 */
export const capitalize = (s: string, from: number, len: number): string => {
  return changeCapitalization(s, from, len, s => s.toLocaleUpperCase());
};

/**
 * Change part of a string to Locale LowerCase, beginning at index 'from' and 'len'
 * nr of characters
 */
export const lowerize = (s: string, from: number, len: number): string => {
  return changeCapitalization(s, from, len, s => s.toLocaleLowerCase());
};

/**
 * Take 'len' number of characters from a string, beginning at index 'from', replacing that part of the
 * string with the result of calling fn on it
 */
export const changeCapitalization = (
  s: string,
  from: number,
  len: number,
  fn: (str: string) => string
): string => {
  return s.slice(0, from) + fn(s.slice(from, from + len)) + s.slice(from + len);
};

/**
 * Calculates and returns the control digit in a swedish social security number
 */
export const getControlDigit = (digits: string): string => {
  const digitList = digits.split('');
  const sum = digitList.reduce((acc, digitText, index) => {
    const digit = Number.parseInt(digitText);
    if (index > 1 && index !== digitList.length - 1) {
      const value = digit * (2 - (index % 2));
      acc += value > 9 ? value - 9 : value;
    }
    return acc;
  }, 0);
  return (10 - (sum % 10)).toString() === '10'
    ? '0'
    : (10 - (sum % 10)).toString();
};
